import RecordFileActions from './RecordFileActions';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaFileAlt, FaInfoCircle, FaTrash, FaUpload } from 'react-icons/fa';
import './PersonRecordControls.css';

export function RecordInfo({ text }) {
  const id = useId();
  const trigger = useRef(null);
  const [position, setPosition] = useState(null);
  const show = () => {
    const rect = trigger.current?.getBoundingClientRect();
    if (rect) setPosition({ left: Math.max(12, Math.min(rect.left, window.innerWidth - 292)), top: Math.max(12, Math.min(rect.bottom + 8, window.innerHeight - 120)) });
  };
  return <><button ref={trigger} type="button" className="record-ui__info" aria-label={text}
    aria-describedby={position ? id : undefined} onMouseEnter={show} onMouseLeave={() => setPosition(null)}
    onFocus={show} onBlur={() => setPosition(null)} onClick={(event) => { event.preventDefault(); event.stopPropagation(); show(); }}
    onKeyDown={(event) => { if (event.key === 'Escape') { event.stopPropagation(); setPosition(null); } }}>
    <FaInfoCircle aria-hidden="true" />
  </button>{position && createPortal(<span id={id} role="tooltip" className="record-ui__tooltip" style={position}>{text}</span>, document.body)}</>;
}

export function RecordDeleteButton({ onClick, disabled = false, title = 'Excluir' }) {
  return <button type="button" className="danger record-ui__delete" onClick={onClick} disabled={disabled}
    title={title} aria-label={title}><FaTrash aria-hidden="true" /><span>Excluir</span></button>;
}

export function fileCategories(files = {}, configured = []) {
  const documents = Array.isArray(files.documents) ? files.documents : [];
  return [...new Set([...(Array.isArray(configured) ? configured : []),
    ...(Array.isArray(files.categories) ? files.categories : []), ...documents.map((doc) => doc.category)]
    .filter((value) => typeof value === 'string' && value.trim()))];
}
export function filterRecordFiles(documents, category) {
  return category === null ? documents : documents.filter((doc) => (doc.category || '') === category);
}
const fileKey = (doc, index) => doc.id == null ? `legacy-${index}` : String(doc.id);


export function RecordFilesPanel({ files = {}, categoryOptions = [], onChange, disabled = false }) {
  const documents = Array.isArray(files.documents) ? files.documents : [];
  const categories = useMemo(() => fileCategories(files, categoryOptions), [files, categoryOptions]);
  const [filter, setFilter] = useState(null);
  const [addedId, setAddedId] = useState(null);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState('');
  const readerRef = useRef(null);
  const alive = useRef(true);
  const categoryRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const tabsId = useId();
  const added = documents.find((doc) => doc.id === addedId);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; readerRef.current?.abort(); };
  }, []);
  useEffect(() => { if (addedId) categoryRef.current?.focus(); }, [addedId]);
  useEffect(() => {
    if (filter !== null && filter !== '' && !categories.includes(filter)) setFilter(null);
  }, [categories, filter]);

  const changeCategory = (id, index, category) => onChange((current = {}) => ({ ...current,
    documents: (current.documents || []).map((doc, i) => fileKey(doc, i) === (id == null ? `legacy-${index}` : String(id)) ? { ...doc, category } : doc),
  }));
  const remove = (id, index) => onChange((current = {}) => ({ ...current,
    documents: (current.documents || []).filter((doc, i) => fileKey(doc, i) !== (id == null ? `legacy-${index}` : String(id))),
  }));
  const selectFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || disabled) return;
    setError(''); setReading(true);
    const reader = new FileReader();
    readerRef.current = reader;
    reader.onerror = () => { if (alive.current) { setReading(false); setError('N\u00e3o foi poss\u00edvel ler o arquivo.'); } };
    reader.onload = () => {
      if (!alive.current) return;
      const id = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const document = { id, name: file.name, category: '', uploadedAt: new Date().toISOString().slice(0, 10),
        dataUrl: String(reader.result || ''), source: 'manual' };
      // Functional update: preserve any field or document edited during FileReader.
      onChangeRef.current((current = {}) => ({ ...current, documents: [...(current.documents || []), document] }));
      setFilter(null); setAddedId(id); setReading(false);
    };
    reader.readAsDataURL(file);
  };
  const tabs = [{ value: null, label: 'Todos' }, ...categories.map((value) => ({ value, label: value })),
    ...(documents.some((doc) => !doc.category) ? [{ value: '', label: 'Sem categoria' }] : [])];
  const visible = filterRecordFiles(documents, filter);
  const selectTabByKey = (event, index) => {
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;
    event.preventDefault(); setFilter(tabs[next].value);
    document.getElementById(`${tabsId}-tab-${next}`)?.focus();
  };
  return <div className="record-ui__files">
    <section className="record-ui__upload">
      <div className="record-ui__heading"><h3><FaUpload aria-hidden="true" />Adicionar arquivo</h3>
        <RecordInfo text="Selecione o arquivo, escolha a categoria e salve o cadastro para confirmar." /></div>
      <input aria-label="Adicionar arquivo" type="file" onChange={selectFile} disabled={disabled || reading} />
      {reading && <span role="status">Carregando arquivo...</span>}
      {added && <div className="record-ui__upload-category"><strong title={added.name}>{added.name}</strong>
        <label><span>Categoria</span><select ref={categoryRef} aria-label="Categoria do novo arquivo" value={added.category || ''}
          disabled={disabled} onChange={(event) => changeCategory(added.id, -1, event.target.value)}>
          <option value="">Selecione</option>{categories.map((category) => <option key={category} value={category}>{category}</option>)}
        </select></label><RecordInfo text={'As categorias s\u00e3o cadastradas em Configura\u00e7\u00f5es. Arquivos sem categoria tamb\u00e9m podem ser salvos.'} /></div>}
      {error && <span role="alert" className="record-ui__file-error">{error}</span>}
    </section>
    <section className="record-ui__library">
      <div className="record-ui__heading"><h3><FaFileAlt aria-hidden="true" />Arquivos</h3>
        <RecordInfo text={'Clique em uma categoria para filtrar. Altera\u00e7\u00f5es de categoria e exclus\u00f5es s\u00e3o confirmadas ao salvar o cadastro.'} /></div>
      <div className="record-ui__category-tabs" role="tablist" aria-label="Categorias de arquivos">
        {tabs.map((tab, index) => <button key={tab.value === null ? 'all' : `category-${tab.value}`} id={`${tabsId}-tab-${index}`}
          type="button" role="tab" aria-selected={filter === tab.value} tabIndex={filter === tab.value ? 0 : -1}
          aria-controls={`${tabsId}-panel`} onClick={() => setFilter(tab.value)} onKeyDown={(event) => selectTabByKey(event, index)}>
          {tab.label}<span>{filterRecordFiles(documents, tab.value).length}</span></button>)}
      </div>
      <div id={`${tabsId}-panel`} role="tabpanel" aria-labelledby={`${tabsId}-tab-${Math.max(0, tabs.findIndex((tab) => tab.value === filter))}`} tabIndex={0}>
        <div className="record-ui__file-list">{visible.map((doc) => {
          const index = documents.indexOf(doc);
          const name = doc.name || doc.fileName || 'Arquivo';
          
          return <article key={fileKey(doc, index)} className="record-ui__file-card"><FaFileAlt aria-hidden="true" />
            <div className="record-ui__file-name"><strong title={name}>{name}</strong></div>
            <label><span>Categoria</span><select aria-label={`Categoria de ${name}`} value={doc.category || ''} disabled={disabled}
              onChange={(event) => changeCategory(doc.id, index, event.target.value)}><option value="">Sem categoria</option>
              {categories.map((category) => <option key={category} value={category}>{category}</option>)}
            </select></label>
            <div className="record-ui__file-actions"><RecordFileActions file={doc} url={doc.dataUrl} name={name} />
              <RecordDeleteButton disabled={disabled} title={`Excluir ${name}`} onClick={() => remove(doc.id, index)} /></div>
          </article>;
        })}</div>
        {!visible.length && <h4 className="record-ui__file-empty">Nenhum arquivo</h4>}
      </div>
    </section>
  </div>;
}

/* record-file-actions-polish-v1 */
